import React from 'react';
import classNames from 'classnames';

import Field, { hasDataInFields } from '../../Field';

import SectionContainer from '../SectionContainer';
import css from './SectionHero.module.css';

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

// The secondary "Get personal offers" CTA is not part of the hero's Console content, so it
// is opted into per page. The ids are the ones PageBuilder receives: the asset name for the
// landing page and the /p/:pageId slug for CMS pages.
const SECONDARY_CTA_PAGE_IDS = ['landing-page', 'about'];

/**
 * Section component for a website's hero section
 * The Section Hero doesn't have any Blocks by default, all the configurations are made in the Section Hero settings
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.defaultClasses
 * @param {string} props.defaultClasses.sectionDetails
 * @param {string} props.defaultClasses.title
 * @param {string} props.defaultClasses.description
 * @param {string} props.defaultClasses.ctaButton
 * @param {string} props.sectionId id of the section
 * @param {string?} props.pageId id of the page the section is rendered on
 * @param {'hero'} props.sectionType
 * @param {Object?} props.title
 * @param {Object?} props.description
 * @param {Object?} props.appearance
 * @param {Object?} props.callToAction
 * @param {Object} props.options extra options for the section component (e.g. custom fieldComponents)
 * @param {Object<string,FieldComponentConfig>?} props.options.fieldComponents custom fields
 * @returns {JSX.Element} Section for article content
 */
const SectionHero = props => {
  const {
    sectionId,
    pageId,
    className,
    rootClassName,
    defaultClasses,
    title,
    description,
    appearance,
    callToAction,
    options,
  } = props;

  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };

  const hasHeaderFields = hasDataInFields([title, description, callToAction], fieldOptions);

  const hasSecondaryCta = SECONDARY_CTA_PAGE_IDS.includes(pageId);
  const secondaryCtaData = {
    fieldType: 'internalButtonLink',
    href: '/personalised_offers',
    content: 'Get personal offers',
  };

  return (
    <SectionContainer
      id={sectionId}
      className={className}
      rootClassName={classNames(rootClassName || css.root)}
      appearance={appearance}
      options={fieldOptions}
    >
      {hasHeaderFields ? (
        <header className={classNames(defaultClasses.sectionDetails, css.sectionDetails)}>
          <Field
            data={title}
            className={classNames(defaultClasses.title, css.title)}
            options={fieldOptions}
          />
          <Field
            data={description}
            className={classNames(defaultClasses.description, css.description)}
            options={fieldOptions}
          />
          <div className={css.ctaContainer}>
            <Field
              data={callToAction}
              className={classNames(defaultClasses.ctaButton, css.ctaButton)}
              options={fieldOptions}
            />
            {hasSecondaryCta ? (
              <Field
                data={secondaryCtaData}
                className={classNames(defaultClasses.ctaButton, css.ctaButton, css.secondaryCta)}
                options={fieldOptions}
              />
            ) : null}
          </div>
        </header>
      ) : null}
    </SectionContainer>
  );
};

export default SectionHero;
